<template>
  <section class="admin-content" id="claims-by-provider">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 28px 0; gap: 12px;">
            <div class="header-icon-wrap mr-1"><i class="fe fe-check-square" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <small style="opacity: 0.8;">
                <router-link to="/all-claims-facility" class="text-white"><i class="fe fe-arrow-left mr-1"></i>Facilities awaiting approval</router-link>
              </small>
              <h4 class="mb-0 font-weight-bold">{{ facilityName || "Claims awaiting approval" }}</h4>
              <small style="opacity: 0.8;">Claims forwarded to the ES for approval for payment</small>
            </div>
            <div v-if="queue.length > 1" class="queue-nav">
              <button class="btn btn-sm btn-light" :disabled="!prevId" @click="goTo(prevId)" title="Previous facility"><i class="fe fe-chevron-left"></i></button>
              <span class="mx-2"><b>{{ queuePosition + 1 }}</b> of {{ queue.length }} facilities</span>
              <button class="btn btn-sm btn-light" :disabled="!nextId" @click="goTo(nextId)" title="Next facility"><i class="fe fe-chevron-right"></i></button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <!-- Summary + approve, always at the top -->
          <div class="card shadow-sm summary-card mb-3">
            <div class="card-body d-flex flex-wrap align-items-center" style="gap: 24px;">
              <div><span class="stat-label">Claims</span><div class="stat-value">{{ fmt(summary.count) }}</div></div>
              <div><span class="stat-label">Amount claimed</span><div class="stat-value">₦{{ fmt(summary.requested_total) }}</div></div>
              <div><span class="stat-label">Amount to approve</span><div class="stat-value text-success">₦{{ fmt(summary.approved_total) }}</div></div>
              <div v-if="summary.oldest"><span class="stat-label">Waiting since</span><div class="stat-value small-value">{{ summary.oldest | moment("D MMM YYYY") }}</div></div>
              <div class="ml-auto text-right">
                <button class="btn btn-approve" :disabled="!selectedIds.length || approving || loading" @click="approve">
                  <span v-if="approving"><span class="spinner-border spinner-border-sm mr-1"></span> Approving…</span>
                  <span v-else><i class="fe fe-credit-card mr-1"></i> Approve {{ fmt(selectedIds.length) }} {{ selectedIds.length === 1 ? "claim" : "claims" }} for payment</span>
                </button>
                <div class="small text-muted mt-1">
                  ₦{{ fmt(selectedAmount) }} selected<span v-if="selectedIds.length < claims.length"> · {{ fmt(claims.length - selectedIds.length) }} held back</span>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm table-card">
            <div class="d-flex flex-wrap align-items-center px-4 pt-4 pb-3" style="gap: 8px;">
              <i class="fe fe-list text-primary mr-1"></i>
              <h6 class="mb-0 font-weight-bold">Claims</h6>
              <span class="badge badge-soft-info">{{ fmt(filtered.length) }}</span>
              <input v-model.trim="search" class="form-control form-control-sm ml-auto search-input" placeholder="Search enrollee, ID, claim no., diagnosis…" />
              <button class="btn btn-sm btn-outline-secondary" :class="{ active: showFilters }" @click="showFilters = !showFilters"><i class="fe fe-calendar mr-1"></i> Dates</button>
              <button class="btn btn-sm btn-outline-primary" :disabled="!claims.length" @click="exportCsv"><i class="fe fe-download mr-1"></i> CSV</button>
            </div>
            <div v-if="showFilters" class="d-flex flex-wrap align-items-end px-4 pb-3" style="gap: 8px;">
              <div><label class="small text-muted mb-1 d-block">Submitted from</label><input type="date" class="form-control form-control-sm" v-model="from" /></div>
              <div><label class="small text-muted mb-1 d-block">to</label><input type="date" class="form-control form-control-sm" v-model="to" /></div>
              <button class="btn btn-sm btn-submit" @click="load">Apply</button>
              <button class="btn btn-sm btn-link" v-if="from || to" @click="from = ''; to = ''; load()">Clear</button>
            </div>

            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background: #f8fafc;">
                  <tr>
                    <th class="table-th" style="width: 36px;"><input type="checkbox" :checked="allVisibleSelected" :indeterminate.prop="someVisibleSelected" @change="toggleVisible($event.target.checked)" title="Select all shown" /></th>
                    <th class="table-th">Submitted</th>
                    <th class="table-th">Claim</th>
                    <th class="table-th">Enrollee</th>
                    <th class="table-th">Diagnosis</th>
                    <th class="table-th">Status</th>
                    <th class="table-th text-right">Claimed</th>
                    <th class="table-th text-right">Approved</th>
                    <th class="table-th"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading"><td colspan="9" class="text-center py-5 text-muted"><span class="spinner-border spinner-border-sm mr-2"></span>Loading all claims…</td></tr>
                  <tr v-else-if="!filtered.length">
                    <td colspan="9" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                      <p class="mt-2 mb-0">{{ claims.length ? "No claims match your search." : "No claims are waiting for approval for this facility." }}</p>
                    </td>
                  </tr>
                  <template v-else>
                    <tr v-for="c in pageRows" :key="c.id" class="table-row" :class="{ 'row-off': !selected[c.id] }">
                      <td><input type="checkbox" v-model="selected[c.id]" /></td>
                      <td class="text-nowrap">{{ c.created_at | moment("D MMM YYYY") }}</td>
                      <td class="small">{{ c.claim_unique_id || "#" + c.id }}<div v-if="c.authorization_code" class="text-muted">{{ c.authorization_code }}</div></td>
                      <td>{{ c.patient.full_name || "—" }}<div class="small text-muted">{{ c.patient.id_card_number }}</div></td>
                      <td class="small">{{ c.diagnosis.name || "—" }}</td>
                      <td><span class="status-badge" :class="status(c).variant">{{ status(c).label }}</span></td>
                      <td class="text-right text-nowrap">₦{{ fmt(c.requested_total_cost) }}</td>
                      <td class="text-right text-nowrap font-weight-bold">₦{{ fmt(c.approved_total_cost) }}</td>
                      <td class="text-right">
                        <a :href="claimLink(c)" target="_blank" class="btn btn-sm btn-outline-primary" title="Open claim in a new tab"><i class="fe fe-eye"></i></a>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <div class="d-flex flex-wrap align-items-center px-4 py-3 border-top" style="gap: 8px;" v-if="filtered.length">
              <small class="text-muted">Showing {{ fmt(pageStart + 1) }}–{{ fmt(Math.min(pageStart + pageRows.length, filtered.length)) }} of {{ fmt(filtered.length) }}</small>
              <select class="form-control form-control-sm ml-auto" style="width: auto;" v-model.number="pageSize" @change="page = 1">
                <option :value="50">50 per page</option><option :value="100">100 per page</option><option :value="250">250 per page</option><option :value="0">All</option>
              </select>
              <button class="btn btn-sm btn-outline-secondary" :disabled="page <= 1" @click="page--">Previous</button>
              <span class="small">Page {{ page }} of {{ pageCount }}</span>
              <button class="btn btn-sm btn-outline-secondary" :disabled="page >= pageCount" @click="page++">Next</button>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import { getClaimStatus } from "@/utils/claimStatus";

/**
 * Claims of one facility awaiting the ES for approval for payment (GET treatclaimsprovider?paginate_value=all).
 * All claims load at once; "Approve for payment" creates one payment order for the ticked claims and then moves to
 * the next facility in the queue (?queue=ids from the facilities list, else the API's oldest-first order).
 */
export default {
  components: { Navbar },
  data() {
    return {
      claims: [], summary: { count: 0, requested_total: 0, approved_total: 0, oldest: null }, selected: {},
      loading: false, approving: false, search: "", showFilters: false, from: "", to: "",
      page: 1, pageSize: 100, facilities: [], facilityName: "",
    };
  },
  computed: {
    providerId() { return Number(this.$route.params.id); },
    queue() {
      const q = this.$route.query.queue;
      if (q) return String(q).split(",").map(Number).filter(Boolean);
      return this.facilities.map((f) => f.id);
    },
    queuePosition() { return this.queue.indexOf(this.providerId); },
    prevId() { return this.queuePosition > 0 ? this.queue[this.queuePosition - 1] : null; },
    nextId() { return this.queuePosition >= 0 && this.queuePosition < this.queue.length - 1 ? this.queue[this.queuePosition + 1] : null; },
    filtered() {
      const s = this.search.toLowerCase();
      if (!s) return this.claims;
      return this.claims.filter((c) => [c.claim_unique_id, c.authorization_code, c.patient.full_name, c.patient.id_card_number, c.diagnosis.name]
        .some((v) => v && String(v).toLowerCase().includes(s)));
    },
    pageCount() { return this.pageSize ? Math.max(1, Math.ceil(this.filtered.length / this.pageSize)) : 1; },
    pageStart() { return this.pageSize ? (this.page - 1) * this.pageSize : 0; },
    pageRows() { return this.pageSize ? this.filtered.slice(this.pageStart, this.pageStart + this.pageSize) : this.filtered; },
    selectedIds() { return this.claims.filter((c) => this.selected[c.id]).map((c) => c.id); },
    selectedAmount() { return this.claims.filter((c) => this.selected[c.id]).reduce((n, c) => n + Number(c.approved_total_cost || 0), 0); },
    allVisibleSelected() { return this.pageRows.length > 0 && this.pageRows.every((c) => this.selected[c.id]); },
    someVisibleSelected() { return !this.allVisibleSelected && this.pageRows.some((c) => this.selected[c.id]); },
  },
  watch: {
    "$route.params.id"() { this.page = 1; this.search = ""; this.facilityName = ""; this.load(); },
    search() { this.page = 1; },
  },
  created() {
    this.load();
    this.loadFacilities();
  },
  methods: {
    fmt(v) { return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
    status(c) { return getClaimStatus(c); },
    claimLink(c) { return this.$router.resolve({ path: `/claim/${c.id}` }).href; },
    load() {
      this.loading = true;
      this.axios.get("/api/v1/auth/treatclaimsprovider", {
        params: { paginate_value: "all", provider_id: this.providerId, date: this.from || this.to ? "date" : undefined, from: this.from || undefined, to: this.to || undefined },
        timeout: 0,
      }).then((res) => {
        this.claims = res.data.data.data || [];
        this.summary = res.data.summary || this.summary;
        const sel = {};
        this.claims.forEach((c) => { sel[c.id] = true; });
        this.selected = sel;
        if (this.claims.length) this.facilityName = this.claims[0].provider.agency_name;
      }).catch(() => {
        this.$toasted.error("Could not load the claims.", { position: "top-center", duration: 4000 });
      }).finally(() => { this.loading = false; });
    },
    loadFacilities() {
      return this.axios.get("/api/v1/auth/claims/awaiting-es").then((res) => {
        this.facilities = res.data.data || [];
        const me = this.facilities.find((f) => f.id === this.providerId);
        if (me && !this.facilityName) this.facilityName = me.agency_name;
        return this.facilities;
      }).catch(() => []);
    },
    toggleVisible(on) {
      const sel = { ...this.selected };
      this.pageRows.forEach((c) => { sel[c.id] = on; });
      this.selected = sel;
    },
    goTo(id) {
      if (id) this.$router.push({ path: `/all-claims/${id}`, query: this.$route.query });
    },
    approve() {
      const n = this.selectedIds.length;
      if (!confirm(`Approve ${n} ${n === 1 ? "claim" : "claims"} of ${this.facilityName || "this facility"} for payment (₦${this.fmt(this.selectedAmount)})? A payment order will be created.`)) return;
      const agency = JSON.parse(localStorage.getItem("agency") || "{}") || {};
      if (!agency.id) agency.id = 90; // this portal's agency
      this.approving = true;
      this.axios.post("/api/v1/auth/paymentorder", {
        agency_id: agency.id, status: "pending", provider_id: this.providerId, type: "claims", payment_method: "offline", claims: this.selectedIds,
      }).then(async () => {
        this.$toasted.success(`Payment order created for ${n} ${n === 1 ? "claim" : "claims"}.`, { position: "top-center", duration: 4000 });
        await this.goToNextFacility();
      }).catch((e) => {
        const d = e.response && e.response.data;
        this.$toasted.error((d && d.message) || "Could not create the payment order.", { position: "top-center", duration: 5000 });
      }).finally(() => { this.approving = false; });
    },
    // Next facility in the queue that still has claims waiting; when none is left, back to the list.
    async goToNextFacility() {
      const fresh = await this.loadFacilities();
      const waiting = new Set(fresh.map((f) => f.id));
      const order = this.$route.query.queue ? this.queue : fresh.map((f) => f.id);
      const pos = order.indexOf(this.providerId);
      const after = pos >= 0 ? order.slice(pos + 1).concat(order.slice(0, pos)) : order;
      const next = after.find((id) => id !== this.providerId && waiting.has(id))
        || fresh.map((f) => f.id).find((id) => id !== this.providerId);
      if (next) {
        const f = fresh.find((x) => x.id === next);
        this.$toasted.info(`Next facility: ${f ? f.agency_name : ""}${f ? ` (${f.claims_count} claims)` : ""}`, { position: "top-center", duration: 4000 });
        this.$router.push({ path: `/all-claims/${next}`, query: this.$route.query });
      } else if (waiting.has(this.providerId)) {
        this.load(); // only claims held back at this facility remain
      } else {
        this.$toasted.success("All done: no facility has claims waiting for approval.", { position: "top-center", duration: 5000 });
        this.$router.push("/all-claims-facility");
      }
    },
    exportCsv() {
      const head = ["Submitted", "Claim", "Authorization code", "Enrollee", "Enrollee ID", "Diagnosis", "Status", "Claimed", "Approved", "Selected"];
      const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const lines = [head.map(esc).join(",")].concat(this.filtered.map((c) => [
        String(c.created_at).slice(0, 10), c.claim_unique_id || c.id, c.authorization_code, c.patient.full_name, c.patient.id_card_number,
        c.diagnosis.name, this.status(c).label, c.requested_total_cost, c.approved_total_cost, this.selected[c.id] ? "yes" : "no",
      ].map(esc).join(",")));
      const url = URL.createObjectURL(new Blob(["﻿" + lines.join("\n")], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `claims_awaiting_approval_${String(this.facilityName || this.providerId).replace(/[^a-z0-9]+/gi, "_")}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); padding-bottom: 40px; }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255, 255, 255, 0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.queue-nav { display: flex; align-items: center; background: rgba(255, 255, 255, 0.12); border-radius: 10px; padding: 6px 8px; }
.summary-card { border-radius: 12px; border: 1px solid #e9ecef; position: sticky; top: 64px; z-index: 5; }
.table-card { border-radius: 12px; border: 1px solid #e9ecef; overflow: hidden; }
.table-th { font-size: 0.73rem; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; white-space: nowrap; }
.table-row:hover { background: #f8fafc; }
.row-off { opacity: 0.5; }
.stat-label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; font-weight: 600; }
.stat-value { font-size: 1.25rem; font-weight: 700; color: #1e293b; line-height: 1.2; }
.small-value { font-size: 1rem; }
.search-input { max-width: 300px; }
.btn-submit, .btn-approve { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-approve { background: linear-gradient(135deg, #047857 0%, #10b981 100%); padding: 10px 18px; }
.btn-submit:hover, .btn-approve:hover { opacity: 0.92; color: #fff; }
.btn-approve:disabled { opacity: 0.5; }
.status-badge { display: inline-block; font-size: 0.72rem; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.status-badge.warning { background: #fef3c7; color: #92400e; }
.status-badge.success { background: #d1fae5; color: #065f46; }
.status-badge.danger { background: #fee2e2; color: #991b1b; }
.status-badge.indigo { background: #e0e7ff; color: #3730a3; }
.status-badge.info { background: #dbeafe; color: #1d4ed8; }
.status-badge.dark { background: #e5e7eb; color: #1f2937; }
.status-badge.forwarded { background: #ede9fe; color: #5b21b6; }
.status-badge.sent-back { background: #ffedd5; color: #9a3412; }
@media (max-width: 768px) { .summary-card { position: static; } .search-input { max-width: none; width: 100%; } }
</style>
